/**
 * Recover tabs that outlived a deploy.
 *
 * Each deploy renames the lazy chunks (e.g. RankingCampaignPage-<hash>.js), and
 * the production domain answers the old names with index.html. A tab opened
 * before the deploy then fails its dynamic import, and the router shows the
 * 404 page until the user reloads. Vite reports these failures as a
 * `vite:preloadError` event, so reload the page once to fetch the new chunks.
 * The event is not cancelled: cancelling makes the import resolve to undefined,
 * which breaks the lazy component with a TypeError instead of the import error.
 * If the import fails again right after that reload, nothing happens.
 */
export type StaleChunkReloadHost = Pick<Window, 'addEventListener' | 'dispatchEvent' | 'sessionStorage' | 'location'>;

const LAST_RELOAD_KEY = 'staleChunkReloadAt';
const RELOAD_GUARD_MS = 10_000;

export function installStaleChunkReload(host: StaleChunkReloadHost = window, now: () => number = Date.now): void {
  host.addEventListener('vite:preloadError', () => {
    const at = now();
    try {
      const lastReload = Number(host.sessionStorage.getItem(LAST_RELOAD_KEY)) || 0;
      if (at - lastReload <= RELOAD_GUARD_MS) return;
      host.sessionStorage.setItem(LAST_RELOAD_KEY, String(at));
    } catch {
      // Without session storage we cannot tell a stale tab from a reload loop.
      return;
    }
    host.location.reload();
  });
}
